import { useSelector } from 'react-redux'
import { useMemo } from 'react'
import {
  selectProChannels,
  selectFuboFreeChannels,
  selectLatinoChannels,
  selectEliteChannels,
  selectCanadaEntertainmentChannels,
  selectCanadaSportsChannels,
  selectCanadaPremiumChannels,
  selectCanadaSportsProChannels,
} from 'src/modules/reducers/products/selectors'

export const channelIds = {
  free: 'free',
  latino: 'latino',
  pro: 'pro',
  elite: 'elite',
  essential: 'essential',
  us_standalone: 'standalone',
  ca_entertainment: 'ca_entertainment',
  ca_sports: 'ca_sports',
  ca_premium: 'ca_premium',
}

const defaultChannelCounts = {
  [channelIds.free]: 100,
  [channelIds.pro]: 150,
  [channelIds.elite]: 200,
  [channelIds.ca_entertainment]: 64,
  [channelIds.ca_sports]: 79,
  [channelIds.ca_premium]: 101,
  [channelIds.ca_sports_pro]: 96,
}

const channelPlanMap = {
  [channelIds.free]: selectFuboFreeChannels,
  [channelIds.latino]: selectLatinoChannels,
  [channelIds.pro]: selectProChannels,
  [channelIds.elite]: selectEliteChannels,
  [channelIds.essential]: selectProChannels,
  [channelIds.us_standalone]: selectFuboFreeChannels,
  [channelIds.ca_entertainment]: selectCanadaEntertainmentChannels,
  [channelIds.ca_sports]: selectCanadaSportsChannels,
  [channelIds.ca_premium]: selectCanadaPremiumChannels,
  [channelIds.ca_sports_pro]: selectCanadaSportsProChannels,
}

const roundToNearest10 = (num) => Math.floor(num / 10) * 10

export const useChannelSelector = (planType) => {
  const selector = useMemo(() => {
    return channelPlanMap[planType] || selectProChannels
  }, [planType])

  const channels = useSelector(selector)
  return channels
}

const defaultSelector = () => []

export const useMultipleChannelSelectors = (channelTypes) => {
  // Memoize the custom selector to prevent recreation on every render
  const customSelector = useMemo(() => {
    return (state) => {
      return channelTypes.map((channelType) => {
        const selector =
          typeof channelPlanMap[channelType] === 'function'
            ? channelPlanMap[channelType]
            : defaultSelector
        return selector(state)
      })
    }
  }, [channelTypes])

  // Use the memoized selector
  const allChannels = useSelector(customSelector)

  // Memoize the channel counts as an object with plan IDs as keys
  const channelCounts = useMemo(() => {
    return channelTypes.reduce((acc, channelType, index) => {
      acc[channelType] = roundToNearest10((allChannels[index] || []).length)
      if (acc[channelType] === 0 && defaultChannelCounts[channelType]) {
        acc[channelType] = roundToNearest10(defaultChannelCounts[channelType])
      }
      return acc
    }, {})
  }, [channelTypes, allChannels])

  return channelCounts
}
